<template>
  <v-container class="pa-4" v-if="organizationPlan && organizationPlan.id">
    <div class="pt-4 pb-6">
      <v-btn text ref="button-toolbar-close" @click="$emit('close')">
        <v-icon>close</v-icon>
        <span>{{ $t('general.close') }}</span>
      </v-btn>
    </div>
    <v-card class="rounded-lg">
      <v-card-title>
        {{ $t('organization_billing.manual_billing.title') }}
      </v-card-title>
      <v-card-text>
        <div class="mb-4">
          <span style="color: #2661ff;">
            {{ $t('organization_billing.manual_billing.subtitle') }}
          </span>
        </div>
        <v-divider />
        <v-row>
          <v-col cols="12" class="d-flex justify-center align-center mt-4">
            <v-menu
              ref="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on }">
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="billing_date"
                    :label="$t('organization_billing.manual_billing.select')"
                    append-icon="calendar_month"
                    readonly
                    v-on="on"
                    outlined
                    class="rounded-lg"
                  >
                  </v-text-field>
                </v-col>
              </template>
              <v-date-picker
                v-model="billing_date"
                type="month"
                :max="manualLimit"
                :min="organizationCreated"
                color="#2661FF"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-form ref="form" v-model="validManual" lazy-validation>
          <!-- AMOUNT -->
          <v-row class="d-flex justify-space-around align-center" no-gutters>
            <v-col cols="6">
              <v-text-field
                v-model="amount"
                :label="`Monto ${organizationPlan.currency.code}`"
                :placeholder="organizationPlan.currency.code"
                type="number"
                outlined
                :rules="rules.amountRules"
                class="rounded-lg"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <!-- Collection fee -->
          <v-row class="d-flex justify-space-around align-center" no-gutters>
            <v-col cols="6">
              <v-text-field
                v-model="collection_fee"
                :label="`Collection Fee ${organizationPlan.currency.code}`"
                :placeholder="organizationPlan.currency.code"
                type="number"
                outlined
                :rules="rules.amountRules"
                class="rounded-lg"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-space-around align-center" no-gutters>
            <v-col cols="6">
              <v-text-field
                v-model="automation_amount"
                :label="`Monto automation ${organizationPlan.currency.code}`"
                :placeholder="organizationPlan.currency.code"
                type="number"
                outlined
                :rules="rules.amountRules"
                class="rounded-lg"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-row class="d-flex justify-space-around align-center" no-gutters>
            <v-col cols="12" md="6" class="pr-2">
              <v-text-field
                v-model="total"
                :label="`Total ${organizationPlan.currency.code}`"
                :placeholder="organizationPlan.currency.code"
                type="number"
                outlined
                readonly
                :rules="rules.amountRules"
                class="rounded-lg"
              >
              </v-text-field>
            </v-col>
            <v-col
              cols="12"
              md="6"
              v-if="organizationPlan.currency.code !== 'USD'"
            >
              <v-text-field
                v-model="usd_total"
                :label="$t('organization_billing.manual_billing.value_usd')"
                placeholder="USD"
                type="number"
                outlined
                :rules="rules.amountRules"
                class="rounded-lg"
              >
              </v-text-field>
            </v-col>
          </v-row>
          <v-card-actions class="px-6 py-6">
            <v-spacer />
            <v-btn
              class="rounded-lg mr-4"
              outlined
              color="#2661FF"
              min-width="100px"
              @click="$emit('close')"
            >
              {{ $t('actions.cancel') }}
            </v-btn>
            <v-btn
              color="#2661FF"
              class="rounded-lg"
              min-width="100px"
              :disabled="!validManual"
              @click="manualBilling"
            >
              <span style="color: #fff;">
                {{ $t('actions.accept') }}
              </span>
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
    <BaseConfirmDialog
      v-if="dialogManualBilling"
      :cancel-text="$t('general.cancel')"
      :accept-text="$t('general.confirm')"
      @close="dialogManualBilling = false"
      @proceed="confirmManualBilling()"
      :title="$t('organization_billing.manual_billing.exists')"
      :description="$t('organization_billing.manual_billing.exists_overwrite')"
    />
    <CreateOrganizationPlan
      v-if="initModal"
      :width="600"
      :title="$t('organization_billing.create.title')"
      :description="$t('organization_billing.create.subtitle')"
      :accept-text="$t('actions.accept')"
      :cancel-text="$t('actions.cancel')"
      @close="initModal = false"
      @proceed="submitInit($event)"
    />
  </v-container>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import CreateOrganizationPlan from '@/components/Organization/Details/OrganizationPlan/CreateOrganizationPlan.vue'
import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue'
import Vue from 'vue'

export default {
  name: 'ManualBilling',
  components: { BaseConfirmDialog, CreateOrganizationPlan },
  props: {
    organization_id: { type: Number, required: true }
  },
  data() {
    return {
      validManual: true,
      dialogManualBilling: false,
      initModal: false,
      billing_date: this.$moment()
        .startOf('month')
        .format('YYYY-MM'),
      manualLimit: this.$moment()
        .endOf('month')
        .format('YYYY-MM'),
      usd_currency: null,

      amount: null,
      collection_fee: null,
      automation_amount: null,
      total: null,
      usd_total: null,
      rules: {
        amountRules: [
          v => !!v || 'This field is required',
          v => /^\d+(\.\d+)?$/.test(v) || 'Number only',
          v =>
            (v && v.toString().split('.').length < 2) ||
            (v && v.toString().split('.')[1].length <= 2) ||
            'No more than 2 digits after the decimal point',
          v => (v && v >= 0) || 'Min should be above 0'
        ]
      }
    }
  },
  computed: {
    ...mapState({
      organizationPlan: ({ organization: { organizationPlan } }) =>
        organizationPlan,
      currencies: ({ countries: { currencies } }) => currencies,
      organizationCreated: ({ organization: { organization } }) =>
        organization.created_at
    })
  },
  created() {
    this.getUSDCurrency()
  },
  watch: {
    amount: 'sumValues',
    collection_fee: 'sumValues',
    automation_amount: 'sumValues',
    total: 'convertLocalCurrency'
  },
  methods: {
    ...mapMutations({
      setUpdateRequired: 'organization/setUpdateRequired'
    }),
    sumValues() {
      this.total =
        Number(this.amount) +
        Number(this.collection_fee) +
        Number(this.automation_amount)
    },
    getUSDCurrency() {
      this.usd_currency = this.currencies.find(
        currency => currency.code === 'USD'
      )
    },
    convertLocalCurrency() {
      if (this.organizationPlan.currency.code !== 'USD') {
        this.usd_total = Number(
          parseFloat(
            (Number(this.total) /
              Number(this.organizationPlan.currency.conversion_amount)) *
              Number(this.usd_currency.conversion_amount)
          ).toFixed(2)
        )
      } else {
        this.usd_total = this.total
      }
    },
    validate() {
      this.$refs.form.validate()
    },
    reset() {
      this.$refs.form.reset()
    },
    resetValidation() {
      this.$refs.form.resetValidation()
    },
    async manualBilling() {
      try {
        if (this.$refs.form.validate()) {
          const { organization_id } = this

          const billing_date = this.$moment(this.billing_date)
            .startOf('month')
            .utc()
            .format('YYYY-MM-DD')

          const res = await axios.post(
            `admin/organizations/${organization_id}/billing/manual/check-exists-billing`,
            { billing_date }
          )

          const { existsBillingOnTheSameDate } = res.data

          if (!existsBillingOnTheSameDate) {
            this.confirmManualBilling()
            return
          }

          this.dialogManualBilling = true
        }
      } catch (error) {
        Vue.noty.error('error')
        Vue.noty.error(this.$t('organization_billing.errors.manual_billing'))
        console.log(error)
      }
    },
    async confirmManualBilling() {
      try {
        const { organization_id } = this

        const billing_date = this.$moment(this.billing_date)
          .startOf('month')
          .utc()
          .format('YYYY-MM-DD')
        const start_date = this.$moment(this.billing_date)
          .subtract(1, 'month')
          .startOf('month')
          .utc()
          .format('YYYY-MM-DD')
        const end_date = this.$moment(this.billing_date)
          .subtract(1, 'month')
          .endOf('month')
          .utc()
          .format('YYYY-MM-DD')

        const payload = {
          plan_id: this.organizationPlan.plan_id,
          plan_automation_id: this.organizationPlan.plan_automation_id,
          fee_percentage: this.organizationPlan.fee_percentage,
          total_usd: this.usd_total,
          total: this.total,
          billing_date,
          start_date,
          end_date,
          currency_id: this.organizationPlan.currency_id,
          amount: this.amount,
          collection_fee: this.collection_fee,
          automation_amount: this.automation_amount,
          pricing_model_id: this.organizationPlan.pricing_model_id
        }

        await axios.post(
          `admin/organizations/${organization_id}/billing/manual`,
          payload
        )

        this.setUpdateRequired(true)

        Vue.noty.success(this.$t('organization_billing.success.manual_billing'))
        this.reset()
        this.dialogManualBilling = false
      } catch (error) {
        this.reset()
        Vue.noty.error(this.$t('organization_billing.errors.manual_billing'))
        console.log(error)
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
